import { useEffect, useMemo } from 'react';

import { TOOL_CONST } from '@/constant';
import {
  Cached,
  DeployedCode,
  Fullscreen,
  VFlex,
  VStack,
  useShortcut,
} from '@/package';
import { useVueronSprint } from '@/provider';
import { useToolMainCameraStore, useToolMenuStore } from '@/store';

import ToolbarGroup from './ToolbarGroup';
import ToolbarMenu from './ToolbarMenu';

const { TOOL_CAMERA_VIEW_MODE } = TOOL_CONST;

const CameraToolbarGroup = () => {
  const { namespace, register, unregister } = useShortcut();
  const { modeIndex, getMode, setMode } = useToolMainCameraStore();
  const { toolMap, findByName, select, selectChildren } = useToolMenuStore();
  const mode = useMemo(
    () => getMode() ?? TOOL_CAMERA_VIEW_MODE.DEFAULT,
    [modeIndex],
  );
  const { isSprintEnabled } = useVueronSprint();

  const fullScreenTool = findByName('fullScreen')!;
  const viewModeTool = findByName('viewMode')!;

  useEffect(() => {
    register(fullScreenTool.command!, () => select(fullScreenTool.id));
    register(viewModeTool.command!, () => select(viewModeTool.id));
    if (isSprintEnabled('vuex.sapporo.VP25SP3')) {
      viewModeTool.children.forEach((child, index) => {
        register(`labeling.screen.changeView${index + 1}` as any, () =>
          selectChildren(viewModeTool.id, child.id),
        );
      });
    }

    return () => {
      unregister(fullScreenTool.command!);
      unregister(viewModeTool.command!);
      if (isSprintEnabled('vuex.sapporo.VP25SP3')) {
        viewModeTool.children.forEach((_, index) => {
          unregister(`labeling.screen.changeView${index + 1}` as any);
        });
      }
    };
  }, [namespace]);

  useEffect(() => {
    const index = viewModeTool.children.findIndex(
      c => c.id === viewModeTool.displayedToolMenu.id,
    );

    setMode(index);
  }, [toolMap]);

  return (
    <ToolbarGroup visibleDivider>
      <ToolbarMenu
        toolMenuItem={viewModeTool}
        onClick={() => select(viewModeTool.id)}
      >
        <VStack className="w-full py-0.5" items="center" gap={0.125}>
          {mode === TOOL_CAMERA_VIEW_MODE.DEFAULT ? (
            <DeployedCode />
          ) : (
            <VFlex
              justify="center"
              items="center"
              className="size-5 rounded-sm bg-primary-500 text-[8px] leading-4 font-semibold text-skyBlue-200 capitalize select-none"
            >
              {mode}
            </VFlex>
          )}
          <Cached className="size-3.5!" />
        </VStack>
      </ToolbarMenu>

      <ToolbarMenu
        toolMenuItem={fullScreenTool}
        onClick={() => select(fullScreenTool.id)}
      >
        <Fullscreen className="size-4!" />
      </ToolbarMenu>
    </ToolbarGroup>
  );
};

export default CameraToolbarGroup;
